import * as React from "react";
import { createPortal } from "react-dom";
import { Button, cn } from "@finster/ui";
import { Check, ChevronDown } from "lucide-react";
import { useAnchoredOverlay } from "../hooks/useAnchoredOverlay";
import type { SkillDensity, SkillSourceFilterValue } from "../types";

const FILTER_OPTIONS: readonly {
  value: SkillSourceFilterValue;
  label: string;
  buttonLabel: string;
}[] = [
  { value: "all", label: "All Skills", buttonLabel: "All" },
  { value: "personal", label: "My Skills", buttonLabel: "My Skills" },
  { value: "system", label: "System Skills", buttonLabel: "System Skills" },
];

export interface SkillSourceFilterProps {
  value: SkillSourceFilterValue;
  onChange: (value: SkillSourceFilterValue) => void;
  density?: SkillDensity;
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

export const SkillSourceFilter: React.FC<SkillSourceFilterProps> = ({
  value,
  onChange,
  density = "default",
  disabled = false,
  open: controlledOpen,
  onOpenChange,
  className,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const generatedId = React.useId();
  const menuId = `skill-source-filter-${generatedId.replace(/:/g, "")}`;
  const position = useAnchoredOverlay({
    open,
    anchorRef: triggerRef,
    width: 128,
    // Three 32px rows, two 4px gaps, 4px padding each side, 1px border each side.
    maxHeight: 114,
    preferredSide: "bottom",
    align: "end",
    gap: 4,
  });
  const selectedOption =
    FILTER_OPTIONS.find((option) => option.value === value) ?? FILTER_OPTIONS[0];

  const setOpen = React.useCallback(
    (nextOpen: boolean) => {
      if (controlledOpen === undefined) setUncontrolledOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [controlledOpen, onOpenChange]
  );

  const close = React.useCallback(
    (restoreFocus = false) => {
      setOpen(false);
      if (restoreFocus) {
        window.setTimeout(() => triggerRef.current?.focus(), 0);
      }
    },
    [setOpen]
  );

  React.useEffect(() => {
    if (disabled && open) close();
  }, [close, disabled, open]);

  React.useEffect(() => {
    if (!open) return undefined;

    const selectedItem = menuRef.current?.querySelector<HTMLButtonElement>(
      '[role="menuitemradio"][aria-checked="true"]'
    );
    selectedItem?.focus();

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        close();
      }
    };
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close(true);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [close, open]);

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>(
        '[role="menuitemradio"]:not(:disabled)'
      ) ?? []
    );
    if (items.length === 0) return;
    const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement);
    let nextIndex: number | null = null;
    if (event.key === "ArrowDown") {
      nextIndex = (currentIndex + 1) % items.length;
    } else if (event.key === "ArrowUp") {
      nextIndex = currentIndex <= 0 ? items.length - 1 : currentIndex - 1;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = items.length - 1;
    }
    if (nextIndex === null) return;
    event.preventDefault();
    items[nextIndex]?.focus();
  };

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        aria-label={`Skill source: ${selectedOption.label}`}
        aria-haspopup="menu"
        aria-controls={open ? menuId : undefined}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className={cn(
          "shrink-0 cursor-pointer gap-1.5 rounded-md bg-background px-2 text-xs",
          density === "compact" ? "h-8" : "h-9",
          className
        )}
      >
        <span className="max-w-24 truncate">{selectedOption.buttonLabel}</span>
        <ChevronDown className="size-4 shrink-0" aria-hidden />
      </Button>

      {open &&
        createPortal(
          <div
            id={menuId}
            ref={menuRef}
            data-skill-source-filter-menu=""
            role="menu"
            aria-label="Skill source"
            onKeyDown={handleMenuKeyDown}
            // gap-1 keeps the selected and hovered backgrounds from meeting and
            // reading as one block, matching the composer picker's filter menu.
            className="fixed z-[2147483647] flex flex-col gap-1 overflow-hidden rounded-lg border border-border bg-background p-1 text-foreground shadow-lg"
            style={{
              left: position?.left,
              top: position?.top,
              bottom: position?.bottom,
              width: position?.width,
              visibility: position ? "visible" : "hidden",
            }}
          >
            {FILTER_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                role="menuitemradio"
                aria-checked={option.value === value}
                tabIndex={-1}
                onClick={() => {
                  onChange(option.value);
                  close(true);
                }}
                className={cn(
                  "flex h-8 w-full shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border-0 bg-transparent px-2 text-left text-xs font-medium",
                  "hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus",
                  option.value === value && "bg-accent"
                )}
              >
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {option.value === value && <Check className="size-4 shrink-0" aria-hidden />}
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};
